<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row" v-if="canViewPsychology">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="m-0 d-flex flex-wrap align-items-end gap-3 toolbar-row">
                            <div id="tickets-table_filter" class="dataTables_filter toolbar-grow">
                                <label class="d-inline-flex align-items-center">
                                    Search: <b-form-input v-model="filter" type="search" placeholder="Search..."
                                        class="form-control form-control-sm ms-2"></b-form-input>
                                </label>
                            </div>
                            <div id="tickets-table_length" class="dataTables_length toolbar-grow">
                                <label class="d-inline-flex align-items-center">
                                    Show <b-form-select class="form-select form-select-sm mx-1" v-model="perPage"
                                        size="sm" :options="Options"></b-form-select> entries
                                </label>
                            </div>
                            <div class="form-group toolbar-date">
                                <label>Date From</label>
                                <input v-model="datefrom" :max="maxAllowedDateFrom" type="date" class="form-control" />
                            </div>
                            <div class="form-group toolbar-date">
                                <label>Date To</label>
                                <input v-model="dateto" type="date" class="form-control" readonly disabled />
                            </div>
                            <div class="toolbar-actions">
                                <b-button @click="FilterData">
                                    <i class="mdi mdi-filter"></i> Filter
                                </b-button>
                                <b-button v-if="showResetButton" variant="outline-danger" @click="ResetFilters">
                                    <i class="mdi mdi-refresh"></i>
                                </b-button>
                                <router-link v-if="canCreatePsychologyPatient" :to="newPatientLink" class="ms-4 btn btn-primary">
                                    <i class="mdi mdi-account-plus"></i> New Patient
                                </router-link>
                            </div>
                        </div>
                        <div v-if="dateRangeError" class="alert alert-warning py-2 px-3 mt-3 mb-0">
                            {{ dateRangeError }}
                        </div>
                        <div class="table-responsive">
                            <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage"
                                :current-page="currentPage" :filter="filter" :filter-included-fields="filterOn"
                                @filtered="Filtered" :busy="IsBusy" empty-text="No data found" empty-filtered-text="No data found">
                                <template #table-busy>
                                    <div class="text-center text-primary my-2"><b-spinner class="align-middle"
                                            type="grow" small></b-spinner> <strong>Loading data...</strong></div>
                                </template>
                                <template #empty>
                                    <div class="psychology-empty-state">
                                        <i class="mdi mdi-account-search-outline psychology-empty-icon"></i>
                                        <div class="fw-semibold">No patients found</div>
                                        <div class="text-muted small">There is no psychology patient data to show here yet.</div>
                                    </div>
                                </template>
                                <template #emptyfiltered>
                                    <div class="psychology-empty-state">
                                        <i class="mdi mdi-filter-off-outline psychology-empty-icon"></i>
                                        <div class="fw-semibold">No matching patients</div>
                                        <div class="text-muted small">Try adjusting the search or date filters.</div>
                                    </div>
                                </template>
                                <template #cell(index)="data">
                                    {{ data.index + 1 }}
                                </template>
                                <template #cell(full_name)="data">
                                    <div class="fw-medium">{{ data.item.full_name }}</div>
                                    <small class="d-block text-primary fw-medium">{{ data.item.gender }}</small>
                                </template>
                                <template #cell(marital_status)="data">
                                    <span>{{ data.item.marital_status }}</span>
                                    <small class="d-block text-primary fw-bold">{{ data.item.age }} years</small>
                                </template>
                                <template #cell(address)="data">
                                    <span>{{ data.item.address }}</span>
                                    <small class="d-block text-primary fw-bold">{{ data.item.residential_status
                                        }}</small>
                                </template>
                                <template #cell(date_of_registration)="data">
                                    <span>{{ formatDisplayDate(data.item.date_of_registration) }}</span>
                                </template>
                                <template #cell(siblings)="data">
                                    <div v-if="data.item.siblings">
                                        {{ data.item.siblings }}
                                    </div>
                                    <span v-else class="text-muted">-</span>
                                </template>
                                <template #cell(actions)="data">
                                    <div class="d-flex align-items-center gap-1 flex-nowrap">
                                    <button class="btn btn-sm btn-outline-info" @click="ViewPatient(data.item)">
                                        <i class="mdi mdi-account-details"></i>
                                    </button>
                                    <button v-if="canEditPsychologyPatient" class="btn btn-sm btn-outline-primary" @click="EditPatient(data.item)">
                                        <i class="mdi mdi-pencil"></i>
                                    </button>
                                    <button v-if="canDeletePsychologyPatient" class="btn btn-sm btn-outline-danger"
                                        @click="deleteConfirm(data.item)">
                                        <i class="mdi mdi-trash-can"></i>
                                    </button>
                                    </div>
                                </template>
                            </b-table>
                            <div v-if="!IsBusy && !Items.length && !filter && !showResetButton" class="psychology-empty-state psychology-empty-state-inline">
                                <i class="mdi mdi-account-search-outline psychology-empty-icon"></i>
                                <div class="fw-semibold">No patients found</div>
                                <div class="text-muted small">There is no psychology patient data to show here yet.</div>
                            </div>
                            <div v-else-if="!IsBusy && !Items.length" class="psychology-empty-state psychology-empty-state-inline">
                                <i class="mdi mdi-filter-off-outline psychology-empty-icon"></i>
                                <div class="fw-semibold">No matching patients</div>
                                <div class="text-muted small">Try adjusting the search or date filters.</div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <div class="dataTables_paginate paging_simple_numbers float-end">
                                    <ul class="pagination pagination-rounded mb-0">
                                        <b-pagination v-model="currentPage" :total-rows="List"
                                            :per-page="perPage"></b-pagination>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="row justify-content-center">
                <div class="col-md-8 col-xl-6">
                    <div>
                        <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br><br>
                        <center><span class="text-primary h6">You have no permission to view psychology patients</span>
                        </center>
                    </div>
                </div>
            </div>
        </div>
        <EditPatientModal ref="editPatientModal" :patientId="selectedPatientId" @patient-updated="onPatientUpdated" />
    </Layout>
</template>

<script>
// import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import EditPatientModal from "./edit_patient.vue";
import { psychopatientsMethods, psychopatientsComputed } from "@/state/helpers";
import axios from "axios";

export default {
    page: {
        title: "Psychology Patients",
        meta: [{ name: "description", content: appConfig.description, },],
    },
    components: {
        Layout, PageHeader, EditPatientModal
    },
    data() {
        return {
            userData: JSON.parse(localStorage.getItem("user")),
            title: "Psychology Patients",
            items: [{ text: "Dashboard", to: "/", }, { text: "Psychology Patients", active: true, },],
            TotalRows: 1,
            currentPage: 1,
            perPage: 10,
            Options: [10, 50, 100, 150, 200],
            filter: null,
            filterOn: [],
            payment_type: '',
            patientvisit: [],
            sortDesc: true,
            isBusy: true,
            dateRangeError: "",
            showResetButton: false,
            siblingsExpanded: {},
            maxSiblingsPreview: 3,
            selectedPatientId: null,
            fields: [
                { key: 'id', label: 'ID', visible: false },
                { key: 'psychology_category_name', label: 'Section', visible: true },
                { key: 'psychology_service_name', label: 'Service', visible: true },
                { key: 'full_name', label: 'Full Name', visible: true },
                { key: 'marital_status', label: 'Marital Status', visible: true },
                { key: 'address', label: 'Address', visible: true },
                // { key: 'nationality', label: 'Nationality', visible: true },
                { key: 'date_of_registration', label: 'Registration Date', visible: true },
                { key: 'actions', label: 'Actions', visible: true },
            ],
            datefrom: '',
            dateto: '',
        };
    },
    computed: {
        ...psychopatientsComputed,
        isAdministrator() {
            return (this.userData?.role || "").toString().toLowerCase() === "administrator";
        },
        activePsychologyCategoryKey() {
            return this.$route.meta?.psychologyCategoryKey || "";
        },
        sectionTitle() {
            const titleMap = {
                psychology_consultations: "Psychology Consultations",
                psychology_group_programs: "Group Therapies & Programs",
                psychology_assessments_tests: "Psychological Assessments & Tests",
                psychology_corporate_services: "Corporate & Institutional Services",
                psychology_sensitive_services: "Specialized & Sensitive Services",
            };

            return titleMap[this.activePsychologyCategoryKey] || "Psychology Patients";
        },
        canViewPsychology() {
            if (!this.activePsychologyCategoryKey) {
                return this.isAdministrator || this.userData?.permissions?.['view_psychology'] == 1;
            }

            return this.isAdministrator || this.userData?.permissions?.[this.activePsychologyCategoryKey] == 1;
        },
        canCreatePsychologyPatient() {
            return this.isAdministrator || this.userData?.permissions?.['create_psychology_patient'] == 1;
        },
        canEditPsychologyPatient() {
            return this.isAdministrator || this.userData?.permissions?.['edit_psychology_patient'] == 1;
        },
        canDeletePsychologyPatient() {
            return this.isAdministrator || this.userData?.permissions?.['delete_psychology_patient'] == 1;
        },
        List() { return this.psychopatients.length; },
        Fields() { return this.fields.filter(field => field.visible) },
        Items() { return this.psychopatients.map((d) => ({ ...d })) },
        IsBusy() { return this.isBusy; },
        todayDate() {
            return new Date().toLocaleDateString('fr-CA');
        },
        sixMonthsAgoDate() {
            const date = new Date();
            date.setMonth(date.getMonth() - 6);
            return date.toLocaleDateString('fr-CA');
        },
        maxAllowedDateFrom() {
            return this.sixMonthsAgoDate;
        },
        newPatientLink() {
            return {
                path: "/psychology/new-patient",
                query: this.activePsychologyCategoryKey ? { category_key: this.activePsychologyCategoryKey } : {},
            };
        },
    },
    mounted() {
        this.applySectionPresentation();
        this.resetDateRange();
        this.loadPsychologyPatients();
    },
    methods: {
        ...psychopatientsMethods,
        applySectionPresentation() {
            this.title = this.sectionTitle;
            this.items = [{ text: "Dashboard", to: "/" }, { text: this.sectionTitle, active: true }];
        },
        resetDateRange() {
            this.dateto = this.todayDate;
            this.datefrom = this.sixMonthsAgoDate;
            this.dateRangeError = "";
        },
        isValidDateRange() {
            const fromDate = new Date(this.datefrom);
            const toDate = new Date(this.dateto);

            if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
                this.dateRangeError = "Please choose a valid six-month date range.";
                return false;
            }

            const minimumFromDate = new Date(toDate);
            minimumFromDate.setMonth(minimumFromDate.getMonth() - 6);

            if (fromDate > minimumFromDate) {
                this.dateRangeError = "Date From must be at least 6 months earlier than the current Date To.";
                return false;
            }

            this.dateRangeError = "";
            return true;
        },
        async loadPsychologyPatients(params = {}) {
            this.isBusy = true;
            try {
                await this.fetchPsychopatients({ category_key: this.activePsychologyCategoryKey, ...params });
                this.TotalRows = this.psychopatients.length;
            } finally {
                this.isBusy = false;
            }
        },
        isSiblingsExpanded(patientId) {
            return !!this.siblingsExpanded[patientId];
        },
        toggleSiblings(patientId) {
            this.$set(this.siblingsExpanded, patientId, !this.isSiblingsExpanded(patientId));
        },
        getSiblingsPreview(item) {
            if (!item || !Array.isArray(item.siblings)) {
                return [];
            }
            if (this.isSiblingsExpanded(item.id)) {
                return item.siblings;
            }
            return item.siblings.slice(0, this.maxSiblingsPreview);
        },
        formatDisplayDate(value) {
            if (!value) {
                return "-";
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        },
        Filtered(filteredItems) {
            this.TotalRows = filteredItems.length;
            this.currentPage = 1;
        },
        FilterData() {
            this.dateto = this.todayDate;
            if (!this.isValidDateRange()) {
                return;
            }
            this.isBusy = true;
            Promise.resolve(this.filterPsychopatients({ datefrom: this.datefrom, dateto: this.dateto, category_key: this.activePsychologyCategoryKey }))
                .finally(() => {
                    this.TotalRows = this.psychopatients.length;
                    this.isBusy = false;
                    this.showResetButton = true;
                });
        },
        ResetFilters() {
            this.resetDateRange();
            this.loadPsychologyPatients();
            this.showResetButton = false;
        },
        onPatientUpdated() {
            this.loadPsychologyPatients();
        },

        ViewPatient(patient) {
            if (patient && patient.id) {
                sessionStorage.setItem("psychoPatientBio", JSON.stringify(patient));
            }
            this.$router.push({
                path: "/psychology/patient",
                query: { id: patient.id },
                state: { patient }
            });
        },

        EditPatient(patient) {
            if (patient && patient.id) {
                sessionStorage.setItem("psychoPatientBio", JSON.stringify(patient));
            }
            this.selectedPatientId = patient?.id || null;
            this.$nextTick(() => {
                this.$refs.editPatientModal?.show();
            });
        },

        deleteConfirm(patient) {
            this.$bvModal.msgBoxConfirm(`Are you sure you want to delete patient ${patient.full_name}? This action cannot be undone.`, {
                title: 'Confirm Deletion',
                size: 'md',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }).then(value => {
                if (value) {
                    this.DeletePatient(patient);
                }
            }).catch(err => {
                alert('Error showing confirmation dialog:', err);
            });
        },

        async DeletePatient(patient) {
            const formData = new FormData();
            formData.append("id", patient.id);

            const response = await axios.post("/delete-psycho-patient", formData);
            if (response.status == 200) {
                this.loadPsychologyPatients();
            }
        },
    }
    ,
    watch: {
        $route() {
            this.applySectionPresentation();
            this.resetDateRange();
            this.loadPsychologyPatients();
        },
    }
    ,
    created() {
        this.resetDateRange();
    }
};
</script>

<style scoped>
.toolbar-row {
    flex-wrap: wrap;
}

.toolbar-grow {
    flex: 1 1 auto;
    min-width: 200px;
}

.toolbar-date {
    flex: 0 0 auto;
    min-width: 150px;
}

.toolbar-actions {
    flex: 0 0 auto;
    display: flex;
    gap: 0.5rem;
}

.toolbar-actions .btn {
    white-space: nowrap;
}

.psychology-empty-state {
    padding: 2rem 1rem;
    text-align: center;
    color: #495057;
}

.psychology-empty-state-inline {
    border-top: 1px solid #eef1f7;
    min-height: 150px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.psychology-empty-icon {
    display: block;
    font-size: 2rem;
    color: #94a3b8;
    margin-bottom: 0.5rem;
}

.siblings-toggle {
    cursor: pointer;
    display: inline-block;
}

@media (max-width: 1200px) {
    .toolbar-grow {
        flex: 1 1 calc(50% - 0.75rem);
    }

    .toolbar-date {
        flex: 1 1 calc(50% - 0.75rem);
    }

    .toolbar-actions {
        flex: 1 1 100%;
        justify-content: flex-end;
    }
}

@media (max-width: 768px) {

    .toolbar-grow,
    .toolbar-date {
        flex: 1 1 100%;
    }

    .toolbar-actions {
        flex: 1 1 100%;
        justify-content: stretch;
    }

    .toolbar-actions .btn {
        flex: 1;
    }
}
</style>
